import { useState, useEffect, useRef } from 'react';
import { useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import api from '../services/api';
import { useAuthStore } from '../store/auth';
import { useBrandingStore } from '../store/branding';
import { SwatchIcon, ArrowPathIcon } from '@heroicons/react/24/outline';

interface ProfileData {
  id: string;
  name: string;
  email: string;
  role?: string;
  avatarUrl: string | null;
  phone?: string;
  bio?: string;
  timezone?: string;
  createdAt: string;
  lastLoginAt?: string;
}

interface ProfileForm {
  name: string;
  email: string;
  phone: string;
  bio: string;
  timezone: string;
}

interface PasswordForm {
  currentPassword: string;
  newPassword: string;
  confirmPassword: string;
}

interface BrandingData {
  id: string;
  tenant_id: string;
  name: string;
  logo_url: string | null;
  favicon_url: string | null;
  primary_color: string;
  secondary_color: string;
  settings: Record<string, any>;
}

interface ColorPreset {
  name: string;
  colors: {
    primaryColor?: string;
    secondaryColor?: string;
    primary_color?: string;
    secondary_color?: string;
  };
}

const TIMEZONES = [
  'UTC',
  'America/New_York',
  'America/Chicago',
  'America/Denver',
  'America/Los_Angeles',
  'Europe/London',
  'Europe/Paris',
  'Europe/Berlin',
  'Asia/Tokyo',
  'Asia/Shanghai',
  'Asia/Dubai',
  'Australia/Sydney',
];

export default function Profile() {
  const [profile, setProfile] = useState<ProfileData | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [changingPassword, setChangingPassword] = useState(false);
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const [activeTab, setActiveTab] = useState<'profile' | 'security' | 'branding'>('profile');
  const fileInputRef = useRef<HTMLInputElement>(null);
  const logoInputRef = useRef<HTMLInputElement>(null);
  const faviconInputRef = useRef<HTMLInputElement>(null);
  const { user, updateUser } = useAuthStore();

  // Branding state
  const [branding, setBranding] = useState<BrandingData | null>(null);
  const [colorPresets, setColorPresets] = useState<ColorPreset[]>([]);
  const [brandingLoading, setBrandingLoading] = useState(false);
  const [savingBranding, setSavingBranding] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [uploadingFavicon, setUploadingFavicon] = useState(false);
  const [primaryColor, setPrimaryColor] = useState('#316D94');
  const [secondaryColor, setSecondaryColor] = useState('#629DBB');
  const [originalColors, setOriginalColors] = useState<{ primary: string; secondary: string } | null>(null);

  const {
    register: registerProfile,
    handleSubmit: handleProfileSubmit,
    reset: resetProfile,
    formState: { errors: profileErrors },
  } = useForm<ProfileForm>();

  const {
    register: registerPassword,
    handleSubmit: handlePasswordSubmit,
    reset: resetPassword,
    watch,
    formState: { errors: passwordErrors },
  } = useForm<PasswordForm>();

  const newPassword = watch('newPassword');

  useEffect(() => {
    fetchProfile();
  }, []);

  useEffect(() => {
    if (activeTab === 'branding') {
      fetchBranding();
    }
  }, [activeTab]);

  const fetchBranding = async () => {
    setBrandingLoading(true);
    try {
      const response = await api.get('/branding');
      if (response.data.success) {
        const { branding: brandingData, presets } = response.data.data;
        setBranding(brandingData);
        setColorPresets(presets || []);
        setPrimaryColor(brandingData.primary_color || '#316D94');
        setSecondaryColor(brandingData.secondary_color || '#629DBB');
      }
    } catch (error: any) {
      if (error.status !== 401) {
        toast.error('Failed to load branding settings');
      }
    } finally {
      setBrandingLoading(false);
    }
  };

  const { updateBranding: updateBrandingStore, applyColors: applyColorsGlobal, primaryColor: storePrimaryColor, secondaryColor: storeSecondaryColor } = useBrandingStore();

  // Function to apply colors as a live preview (without saving to store)
  const applyColorsPreview = (primary: string, secondary: string) => {
    const root = document.documentElement;

    // Helper functions for color conversion
    const hexToHSL = (hex: string): { h: number; s: number; l: number } => {
      const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
      if (!result) return { h: 0, s: 0, l: 0 };
      let r = parseInt(result[1], 16) / 255;
      let g = parseInt(result[2], 16) / 255;
      let b = parseInt(result[3], 16) / 255;
      const max = Math.max(r, g, b);
      const min = Math.min(r, g, b);
      let h = 0, s = 0;
      const l = (max + min) / 2;
      if (max !== min) {
        const d = max - min;
        s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
        switch (max) {
          case r: h = ((g - b) / d + (g < b ? 6 : 0)) / 6; break;
          case g: h = ((b - r) / d + 2) / 6; break;
          case b: h = ((r - g) / d + 4) / 6; break;
        }
      }
      return { h: h * 360, s: s * 100, l: l * 100 };
    };

    const HSLToHex = (h: number, s: number, l: number): string => {
      s /= 100;
      l /= 100;
      const a = s * Math.min(l, 1 - l);
      const f = (n: number) => {
        const k = (n + h / 30) % 12;
        const color = l - a * Math.max(Math.min(k - 3, 9 - k, 1), -1);
        return Math.round(255 * color).toString(16).padStart(2, '0');
      };
      return `#${f(0)}${f(8)}${f(4)}`;
    };

    const generateShades = (baseColor: string): Record<string, string> => {
      const { h, s } = hexToHSL(baseColor);
      return {
        50: HSLToHex(h, Math.max(s - 30, 10), 97),
        100: HSLToHex(h, Math.max(s - 20, 15), 94),
        200: HSLToHex(h, Math.max(s - 10, 20), 86),
        300: HSLToHex(h, s, 76),
        400: HSLToHex(h, s, 64),
        500: HSLToHex(h, s, 50),
        600: HSLToHex(h, s, 42),
        700: HSLToHex(h, s, 34),
        800: HSLToHex(h, s, 28),
        900: HSLToHex(h, s, 22),
      };
    };

    // Apply primary color shades
    const primaryShades = generateShades(primary);
    Object.entries(primaryShades).forEach(([shade, color]) => {
      root.style.setProperty(`--color-primary-${shade}`, color);
    });

    // Apply secondary color shades
    const secondaryShades = generateShades(secondary);
    Object.entries(secondaryShades).forEach(([shade, color]) => {
      root.style.setProperty(`--color-secondary-${shade}`, color);
    });

    root.style.setProperty('--color-primary', primary);
    root.style.setProperty('--color-secondary', secondary);
  };

  // Save original colors when entering branding tab and apply preview on color changes
  useEffect(() => {
    if (activeTab === 'branding' && !originalColors) {
      setOriginalColors({
        primary: storePrimaryColor,
        secondary: storeSecondaryColor
      });
    }
  }, [activeTab, originalColors, storePrimaryColor, storeSecondaryColor]);

  // Apply colors in real-time when user changes them
  useEffect(() => {
    if (activeTab === 'branding' && primaryColor && secondaryColor) {
      applyColorsPreview(primaryColor, secondaryColor);
    }
  }, [primaryColor, secondaryColor, activeTab]);


  const handleSaveBranding = async () => {
    setSavingBranding(true);
    try {
      const response = await api.put('/branding', {
        primary_color: primaryColor,
        secondary_color: secondaryColor,
      });
      if (response.data.success) {
        setBranding(response.data.data);
        // Sync with global branding store to update UI immediately
        updateBrandingStore({
          primaryColor: response.data.data.primary_color,
          secondaryColor: response.data.data.secondary_color,
        });
        // Update original colors to the new saved values
        setOriginalColors({
          primary: response.data.data.primary_color,
          secondary: response.data.data.secondary_color,
        });
        toast.success('Branding updated successfully');
      }
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Failed to update branding');
    } finally {
      setSavingBranding(false);
    }
  };

  const handleLogoUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!['image/png', 'image/svg+xml', 'image/webp', 'image/jpeg', 'image/x-icon'].includes(file.type)) {
      toast.error('Please use PNG, SVG, WEBP, JPG or ICO files');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      toast.error('Logo must be less than 5MB');
      return;
    }

    setUploadingLogo(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await api.post('/branding/logo', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      if (response.data.success) {
        const newLogoUrl = response.data.data.url;
        setBranding((prev) => prev ? { ...prev, logo_url: newLogoUrl } : null);
        // Sync with global branding store to update sidebar logo immediately
        updateBrandingStore({ logoUrl: newLogoUrl });
        toast.success('Logo uploaded successfully');
      }
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Failed to upload logo');
    } finally {
      setUploadingLogo(false);
    }
  };

  const handleDeleteLogo = async () => {
    if (!confirm('Are you sure you want to remove the logo?')) return;

    try {
      await api.delete('/branding/logo');
      setBranding((prev) => prev ? { ...prev, logo_url: null } : null);
      // Sync with global branding store
      updateBrandingStore({ logoUrl: null });
      toast.success('Logo removed');
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Failed to remove logo');
    }
  };

  const handleFaviconUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!['image/png', 'image/x-icon', 'image/vnd.microsoft.icon'].includes(file.type)) {
      toast.error('Please use PNG or ICO files for favicon');
      return;
    }

    if (file.size > 100 * 1024) {
      toast.error('Favicon must be less than 100KB');
      return;
    }

    setUploadingFavicon(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await api.post('/branding/favicon', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      if (response.data.success) {
        const newFaviconUrl = response.data.data.url;
        setBranding((prev) => prev ? { ...prev, favicon_url: newFaviconUrl } : null);
        // Sync with global branding store
        updateBrandingStore({ faviconUrl: newFaviconUrl });
        toast.success('Favicon uploaded successfully');
      }
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Failed to upload favicon');
    } finally {
      setUploadingFavicon(false);
    }
  };

  const handleDeleteFavicon = async () => {
    if (!confirm('Are you sure you want to remove the favicon?')) return;

    try {
      await api.delete('/branding/favicon');
      setBranding((prev) => prev ? { ...prev, favicon_url: null } : null);
      // Sync with global branding store
      updateBrandingStore({ faviconUrl: null });
      toast.success('Favicon removed');
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Failed to remove favicon');
    }
  };

  const handleResetBranding = async () => {
    if (!confirm('Are you sure you want to reset branding to defaults?')) return;

    try {
      const response = await api.post('/branding/reset');
      if (response.data.success) {
        setBranding(response.data.data);
        setPrimaryColor(response.data.data.primary_color);
        setSecondaryColor(response.data.data.secondary_color);
        // Sync with global branding store
        updateBrandingStore({
          logoUrl: null,
          primaryColor: response.data.data.primary_color,
          secondaryColor: response.data.data.secondary_color,
        });
        // Update original colors to the reset values
        setOriginalColors({
          primary: response.data.data.primary_color,
          secondary: response.data.data.secondary_color,
        });
        toast.success('Branding reset to defaults');
      }
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Failed to reset branding');
    }
  };

  const applyPreset = (preset: ColorPreset) => {
    const primary = preset.colors.primaryColor || preset.colors.primary_color || '#316D94';
    const secondary = preset.colors.secondaryColor || preset.colors.secondary_color || '#629DBB';
    setPrimaryColor(primary);
    setSecondaryColor(secondary);
  };

  // Handle tab change - restore colors if leaving branding without saving
  const handleTabChange = (tab: 'profile' | 'security' | 'branding') => {
    if (activeTab === 'branding' && tab !== 'branding' && originalColors) {
      // Check if there are unsaved changes
      if (primaryColor !== storePrimaryColor || secondaryColor !== storeSecondaryColor) {
        // Restore original colors
        applyColorsGlobal();
      }
      setOriginalColors(null);
    }
    setActiveTab(tab);
  };

  const fetchProfile = async () => {
    try {
      const response = await api.get('/auth/profile');
      setProfile(response.data.profile);
      resetProfile({
        name: response.data.profile.name,
        email: response.data.profile.email,
        phone: response.data.profile.phone || '',
        bio: response.data.profile.bio || '',
        timezone: response.data.profile.timezone || 'UTC',
      });
    } catch (error) {
      toast.error('Failed to load profile');
    } finally {
      setLoading(false);
    }
  };

  const onProfileSubmit = async (data: ProfileForm) => {
    setSaving(true);
    try {
      const response = await api.put('/auth/profile', data);
      setProfile(response.data.profile);
      updateUser(response.data.profile);
      toast.success('Profile updated successfully');
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  const onPasswordSubmit = async (data: PasswordForm) => {
    setChangingPassword(true);
    try {
      await api.post('/auth/change-password', {
        currentPassword: data.currentPassword,
        newPassword: data.newPassword,
      });
      resetPassword();
      toast.success('Password changed successfully');
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to change password');
    } finally {
      setChangingPassword(false);
    }
  };

  const handleAvatarChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!['image/jpeg', 'image/png', 'image/gif', 'image/webp'].includes(file.type)) {
      toast.error('Please select a valid image file (JPEG, PNG, GIF, or WebP)');
      return;
    }

    // Validate file size (5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image must be less than 5MB');
      return;
    }

    setUploadingAvatar(true);
    const formData = new FormData();
    formData.append('avatar', file);

    try {
      const response = await api.post('/auth/profile/avatar', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setProfile((prev) => prev ? { ...prev, avatarUrl: response.data.avatarUrl } : null);
      updateUser({ avatarUrl: response.data.avatarUrl });
      toast.success('Avatar updated successfully');
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to upload avatar');
    } finally {
      setUploadingAvatar(false);
    }
  };

  const handleDeleteAvatar = async () => {
    if (!confirm('Are you sure you want to remove your avatar?')) return;

    try {
      await api.delete('/auth/profile/avatar');
      setProfile((prev) => prev ? { ...prev, avatarUrl: null } : null);
      updateUser({ avatarUrl: null });
      toast.success('Avatar removed');
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to remove avatar');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto">
      <h1 className="text-2xl font-bold text-[var(--text-primary)] mb-6">Profile Settings</h1>

      {/* Tabs */}
      <div className="border-b border-[var(--border-color)] mb-6">
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => handleTabChange('profile')}
            className={`py-4 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'profile'
                ? 'border-primary-500 text-primary-600'
                : 'border-transparent text-[var(--text-muted)] hover:text-[var(--text-secondary)] hover:border-[var(--border-color)]'
            }`}
          >
            Profile Information
          </button>
          <button
            onClick={() => handleTabChange('security')}
            className={`py-4 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'security'
                ? 'border-primary-500 text-primary-600'
                : 'border-transparent text-[var(--text-muted)] hover:text-[var(--text-secondary)] hover:border-[var(--border-color)]'
            }`}
          >
            Security
          </button>
          {(user?.role === 'super_admin' || user?.role === 'SUPER_ADMIN') && (
            <button
              onClick={() => handleTabChange('branding')}
              className={`py-4 px-1 border-b-2 font-medium text-sm flex items-center gap-2 ${
                activeTab === 'branding'
                  ? 'border-primary-500 text-primary-600'
                  : 'border-transparent text-[var(--text-muted)] hover:text-[var(--text-secondary)] hover:border-[var(--border-color)]'
              }`}
            >
              <SwatchIcon className="h-5 w-5" />
              Branding
            </button>
          )}
        </nav>
      </div>

      {activeTab === 'profile' && (
        <div className="bg-[var(--bg-secondary)] shadow rounded-lg">
          {/* Avatar Section */}
          <div className="p-6 border-b border-[var(--border-color)]">
            <h2 className="text-lg font-medium text-[var(--text-primary)] mb-4">Profile Photo</h2>
            <div className="flex items-center space-x-6">
              <div className="relative">
                {profile?.avatarUrl ? (
                  <img
                    src={profile.avatarUrl}
                    alt="Profile"
                    className="h-24 w-24 rounded-full object-cover"
                  />
                ) : (
                  <div className="h-24 w-24 rounded-full bg-primary-600 flex items-center justify-center text-white text-3xl font-bold">
                    {profile?.name?.charAt(0).toUpperCase() || 'A'}
                  </div>
                )}
                {uploadingAvatar && (
                  <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-50 rounded-full">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white"></div>
                  </div>
                )}
              </div>
              <div>
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleAvatarChange}
                  accept="image/jpeg,image/png,image/gif,image/webp"
                  className="hidden"
                />
                <button
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploadingAvatar}
                  className="px-4 py-2 bg-[var(--bg-secondary)] border border-[var(--border-color)] rounded-md text-sm font-medium text-[var(--text-secondary)] hover:bg-[var(--bg-secondary)] disabled:opacity-50"
                >
                  Change photo
                </button>
                {profile?.avatarUrl && (
                  <button
                    onClick={handleDeleteAvatar}
                    className="ml-3 px-4 py-2 text-sm font-medium text-red-600 hover:text-red-700"
                  >
                    Remove
                  </button>
                )}
                <p className="mt-2 text-xs text-[var(--text-muted)]">
                  JPG, PNG, GIF or WebP. Max 5MB.
                </p>
              </div>
            </div>
          </div>

          {/* Profile Form */}
          <form onSubmit={handleProfileSubmit(onProfileSubmit)} className="p-6 space-y-6">
            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
              <div>
                <label className="block text-sm font-medium text-[var(--text-secondary)]">Name</label>
                <input
                  {...registerProfile('name', { required: 'Name is required' })}
                  type="text"
                  className="mt-1 block w-full rounded-md border-[var(--border-color)] shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                />
                {profileErrors.name && (
                  <p className="text-red-500 text-xs mt-1">{profileErrors.name.message}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-[var(--text-secondary)]">Email</label>
                <input
                  {...registerProfile('email', {
                    required: 'Email is required',
                    pattern: {
                      value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                      message: 'Invalid email address',
                    },
                  })}
                  type="email"
                  className="mt-1 block w-full rounded-md border-[var(--border-color)] shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                />
                {profileErrors.email && (
                  <p className="text-red-500 text-xs mt-1">{profileErrors.email.message}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-[var(--text-secondary)]">Phone</label>
                <input
                  {...registerProfile('phone')}
                  type="tel"
                  className="mt-1 block w-full rounded-md border-[var(--border-color)] shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-[var(--text-secondary)]">Timezone</label>
                <select
                  {...registerProfile('timezone')}
                  className="mt-1 block w-full rounded-md border-[var(--border-color)] shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                >
                  {TIMEZONES.map((tz) => (
                    <option key={tz} value={tz}>
                      {tz}
                    </option>
                  ))}
                </select>
              </div>

              <div className="sm:col-span-2">
                <label className="block text-sm font-medium text-[var(--text-secondary)]">Bio</label>
                <textarea
                  {...registerProfile('bio')}
                  rows={3}
                  className="mt-1 block w-full rounded-md border-[var(--border-color)] shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                  placeholder="A short bio about yourself"
                />
              </div>
            </div>

            <div className="flex justify-end">
              <button
                type="submit"
                disabled={saving}
                className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 disabled:opacity-50"
              >
                {saving ? 'Saving...' : 'Save changes'}
              </button>
            </div>
          </form>
        </div>
      )}

      {activeTab === 'security' && (
        <div className="bg-[var(--bg-secondary)] shadow rounded-lg">
          <div className="p-6">
            <h2 className="text-lg font-medium text-[var(--text-primary)] mb-4">Change Password</h2>
            <form onSubmit={handlePasswordSubmit(onPasswordSubmit)} className="space-y-6 max-w-md">
              <div>
                <label className="block text-sm font-medium text-[var(--text-secondary)]">
                  Current Password
                </label>
                <input
                  {...registerPassword('currentPassword', {
                    required: 'Current password is required',
                  })}
                  type="password"
                  autoComplete="current-password"
                  className="mt-1 block w-full rounded-md border-[var(--border-color)] shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                />
                {passwordErrors.currentPassword && (
                  <p className="text-red-500 text-xs mt-1">
                    {passwordErrors.currentPassword.message}
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-[var(--text-secondary)]">
                  New Password
                </label>
                <input
                  {...registerPassword('newPassword', {
                    required: 'New password is required',
                    minLength: {
                      value: 8,
                      message: 'Password must be at least 8 characters',
                    },
                  })}
                  type="password"
                  autoComplete="new-password"
                  className="mt-1 block w-full rounded-md border-[var(--border-color)] shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                />
                {passwordErrors.newPassword && (
                  <p className="text-red-500 text-xs mt-1">
                    {passwordErrors.newPassword.message}
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-[var(--text-secondary)]">
                  Confirm New Password
                </label>
                <input
                  {...registerPassword('confirmPassword', {
                    required: 'Please confirm your password',
                    validate: (value) =>
                      value === newPassword || 'Passwords do not match',
                  })}
                  type="password"
                  autoComplete="new-password"
                  className="mt-1 block w-full rounded-md border-[var(--border-color)] shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                />
                {passwordErrors.confirmPassword && (
                  <p className="text-red-500 text-xs mt-1">
                    {passwordErrors.confirmPassword.message}
                  </p>
                )}
              </div>

              <div>
                <button
                  type="submit"
                  disabled={changingPassword}
                  className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 disabled:opacity-50"
                >
                  {changingPassword ? 'Changing...' : 'Change password'}
                </button>
              </div>
            </form>
          </div>

          {/* Account Info */}
          <div className="p-6 border-t border-[var(--border-color)]">
            <h2 className="text-lg font-medium text-[var(--text-primary)] mb-4">Account Information</h2>
            <dl className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div>
                <dt className="text-sm font-medium text-[var(--text-muted)]">Role</dt>
                <dd className="mt-1 text-sm text-[var(--text-primary)] capitalize">
                  {profile?.role || 'Admin'}
                </dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-[var(--text-muted)]">Member since</dt>
                <dd className="mt-1 text-sm text-[var(--text-primary)]">
                  {profile?.createdAt
                    ? new Date(profile.createdAt).toLocaleDateString()
                    : 'N/A'}
                </dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-[var(--text-muted)]">Last login</dt>
                <dd className="mt-1 text-sm text-[var(--text-primary)]">
                  {profile?.lastLoginAt
                    ? new Date(profile.lastLoginAt).toLocaleString()
                    : 'N/A'}
                </dd>
              </div>
            </dl>
          </div>
        </div>
      )}

      {activeTab === 'branding' && (user?.role === 'super_admin' || user?.role === 'SUPER_ADMIN') && (
        <div className="space-y-6">
          {brandingLoading ? (
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
            </div>
          ) : (
            <>
              {/* Logo & Favicon Section */}
              <div className="bg-[var(--bg-secondary)] shadow rounded-lg p-6">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                  {/* Logo */}
                  <div>
                    <h2 className="text-lg font-medium text-[var(--text-primary)] mb-4">Logo</h2>
                    <div className="flex items-center space-x-6">
                      <div className="relative">
                        {branding?.logo_url ? (
                          <img
                            src={branding.logo_url}
                            alt="Logo"
                            className="h-20 max-w-[200px] object-contain border border-[var(--border-color)] rounded-lg p-2 bg-[var(--bg-secondary)]"
                          />
                        ) : (
                          <div className="h-20 w-48 rounded-lg bg-[var(--bg-tertiary)] flex items-center justify-center text-[var(--text-muted)] border-2 border-dashed border-[var(--border-color)]">
                            No logo uploaded
                          </div>
                        )}
                        {uploadingLogo && (
                          <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-50 rounded-lg">
                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white"></div>
                          </div>
                        )}
                      </div>
                      <div>
                        <input
                          type="file"
                          ref={logoInputRef}
                          onChange={handleLogoUpload}
                          accept="image/png,image/svg+xml,image/webp,image/jpeg,image/x-icon"
                          className="hidden"
                        />
                        <button
                          onClick={() => logoInputRef.current?.click()}
                          disabled={uploadingLogo}
                          className="px-4 py-2 bg-[var(--bg-secondary)] border border-[var(--border-color)] rounded-md text-sm font-medium text-[var(--text-secondary)] hover:bg-[var(--bg-secondary)] disabled:opacity-50"
                        >
                          {branding?.logo_url ? 'Change logo' : 'Upload logo'}
                        </button>
                        {branding?.logo_url && (
                          <button
                            onClick={handleDeleteLogo}
                            className="ml-3 px-4 py-2 text-sm font-medium text-red-600 hover:text-red-700"
                          >
                            Remove
                          </button>
                        )}
                        <p className="mt-2 text-xs text-[var(--text-muted)]">
                          PNG, SVG, WEBP, JPG or ICO. Max 5MB. Recommended: 400x120px
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Favicon */}
                  <div>
                    <h2 className="text-lg font-medium text-[var(--text-primary)] mb-4">Favicon</h2>
                    <div className="flex items-center space-x-6">
                      <div className="relative">
                        {branding?.favicon_url ? (
                          <img
                            src={branding.favicon_url}
                            alt="Favicon"
                            className="h-16 w-16 object-contain border border-[var(--border-color)] rounded-lg p-2 bg-[var(--bg-secondary)]"
                          />
                        ) : (
                          <div className="h-16 w-16 rounded-lg bg-[var(--bg-tertiary)] flex items-center justify-center text-[var(--text-muted)] border-2 border-dashed border-[var(--border-color)]">
                            <svg className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                            </svg>
                          </div>
                        )}
                        {uploadingFavicon && (
                          <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-50 rounded-lg">
                            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-white"></div>
                          </div>
                        )}
                      </div>
                      <div>
                        <input
                          type="file"
                          ref={faviconInputRef}
                          onChange={handleFaviconUpload}
                          accept="image/png,image/x-icon,image/vnd.microsoft.icon"
                          className="hidden"
                        />
                        <button
                          onClick={() => faviconInputRef.current?.click()}
                          disabled={uploadingFavicon}
                          className="px-4 py-2 bg-[var(--bg-secondary)] border border-[var(--border-color)] rounded-md text-sm font-medium text-[var(--text-secondary)] hover:bg-[var(--bg-secondary)] disabled:opacity-50"
                        >
                          {branding?.favicon_url ? 'Change favicon' : 'Upload favicon'}
                        </button>
                        {branding?.favicon_url && (
                          <button
                            onClick={handleDeleteFavicon}
                            className="ml-3 px-4 py-2 text-sm font-medium text-red-600 hover:text-red-700"
                          >
                            Remove
                          </button>
                        )}
                        <p className="mt-2 text-xs text-[var(--text-muted)]">
                          PNG or ICO. Max 100KB. Recommended: 32x32 or 64x64px
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Color Palette Section */}
              <div className="bg-[var(--bg-secondary)] shadow rounded-lg p-6">
                <h2 className="text-lg font-medium text-[var(--text-primary)] mb-4">Color Palette</h2>

                {/* Color Inputs */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                  <div>
                    <label className="block text-sm font-medium text-[var(--text-secondary)] mb-2">
                      Primary Color
                    </label>
                    <div className="flex items-center space-x-3">
                      <input
                        type="color"
                        value={primaryColor}
                        onChange={(e) => setPrimaryColor(e.target.value)}
                        className="h-10 w-14 rounded border border-[var(--border-color)] cursor-pointer"
                      />
                      <input
                        type="text"
                        value={primaryColor}
                        onChange={(e) => setPrimaryColor(e.target.value)}
                        pattern="^#[0-9A-Fa-f]{6}$"
                        className="flex-1 rounded-md border-[var(--border-color)] shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm uppercase"
                        placeholder="#316D94"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[var(--text-secondary)] mb-2">
                      Secondary Color
                    </label>
                    <div className="flex items-center space-x-3">
                      <input
                        type="color"
                        value={secondaryColor}
                        onChange={(e) => setSecondaryColor(e.target.value)}
                        className="h-10 w-14 rounded border border-[var(--border-color)] cursor-pointer"
                      />
                      <input
                        type="text"
                        value={secondaryColor}
                        onChange={(e) => setSecondaryColor(e.target.value)}
                        pattern="^#[0-9A-Fa-f]{6}$"
                        className="flex-1 rounded-md border-[var(--border-color)] shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm uppercase"
                        placeholder="#629DBB"
                      />
                    </div>
                  </div>
                </div>

                {/* Color Presets */}
                <div className="mb-6">
                  <label className="block text-sm font-medium text-[var(--text-secondary)] mb-3">
                    Quick Presets
                  </label>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                    {colorPresets.map((preset) => {
                      const presetPrimary = preset.colors.primaryColor || preset.colors.primary_color;
                      const presetSecondary = preset.colors.secondaryColor || preset.colors.secondary_color;
                      return (
                        <button
                          key={preset.name}
                          onClick={() => applyPreset(preset)}
                          className="flex items-center space-x-2 p-2 rounded-lg border border-[var(--border-color)] hover:border-primary-500 hover:bg-[var(--bg-secondary)] transition-colors"
                        >
                          <div className="flex -space-x-1">
                            <div
                              className="w-6 h-6 rounded-full border-2 border-white shadow"
                              style={{ backgroundColor: presetPrimary }}
                            />
                            <div
                              className="w-6 h-6 rounded-full border-2 border-white shadow"
                              style={{ backgroundColor: presetSecondary }}
                            />
                          </div>
                          <span className="text-xs font-medium text-[var(--text-secondary)] truncate">
                            {preset.name}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Preview */}
                <div className="mb-6">
                  <label className="block text-sm font-medium text-[var(--text-secondary)] mb-3">
                    Preview
                  </label>
                  <div className="border border-[var(--border-color)] rounded-lg p-4 bg-[var(--bg-secondary)]">
                    <div className="flex items-center space-x-4 mb-4">
                      <div
                        className="h-10 px-6 rounded-md flex items-center justify-center text-white font-medium text-sm"
                        style={{ backgroundColor: primaryColor }}
                      >
                        Primary Button
                      </div>
                      <div
                        className="h-10 px-6 rounded-md flex items-center justify-center text-white font-medium text-sm"
                        style={{ backgroundColor: secondaryColor }}
                      >
                        Secondary Button
                      </div>
                    </div>
                    <div className="flex items-center space-x-4">
                      <div
                        className="h-2 w-24 rounded-full"
                        style={{ backgroundColor: primaryColor }}
                      />
                      <div
                        className="h-2 w-16 rounded-full"
                        style={{ backgroundColor: secondaryColor }}
                      />
                      <span className="text-sm" style={{ color: primaryColor }}>
                        Primary Text
                      </span>
                      <span className="text-sm" style={{ color: secondaryColor }}>
                        Secondary Text
                      </span>
                    </div>
                  </div>
                </div>

                {/* Save Button */}
                <div className="flex justify-between items-center pt-4 border-t border-[var(--border-color)]">
                  <button
                    onClick={handleResetBranding}
                    className="flex items-center space-x-2 px-4 py-2 text-sm font-medium text-[var(--text-secondary)] hover:text-[var(--text-primary)]"
                  >
                    <ArrowPathIcon className="h-4 w-4" />
                    <span>Reset to Defaults</span>
                  </button>
                  <button
                    onClick={handleSaveBranding}
                    disabled={savingBranding}
                    className="px-6 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 disabled:opacity-50 font-medium"
                  >
                    {savingBranding ? 'Saving...' : 'Save Changes'}
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}
